import type { ProductComparativePriceUnit } from '../../../../models/ecommerce/product/ApiProduct';
import type { ProductAttributeLabel } from '../../../common/models/props/itemTeaserProps';

import { Helpers } from '../../../../utility/helpers';
import { type PriceData, PriceType } from '../../../common/models/priceData/priceData';
import { getSustainabilityA11yLabelFromScores } from '../SustainabilityLights';

export const getProductCustomerInformationText = (product: {
    consumerInformationText: string[] | undefined;
}) => {
    if (!product.consumerInformationText?.length) return null;

    return {
        visual: product.consumerInformationText.join('. '),
        a11y: product.consumerInformationText.join(', '),
    };
};

export const getProductOriginalPriceText = (
    product: {
        priceData: PriceData | undefined;
    },
    priceType: PriceType,
) => {
    const price = product.priceData;
    if (!price) return null;

    return getOriginalPriceText(price, priceType);
};

export const getPrimaryPriceText = (priceData: PriceData | undefined, priceType: PriceType) => {
    if (!priceData) return null;
    const price = Helpers.getPrice(priceData, priceType);
    if (!price) return null;

    return {
        visual: `${Helpers.toEcommercePriceWithCurrency(price, 'hideRest')}/st`,
        a11y: `${Helpers.toEcommercePriceWithCurrencyAriaLabel(price)} styck`,
    };
};

export const getOriginalPriceText = (priceData: PriceData, priceType: PriceType) => {
    const price = Helpers.getPrice(priceData, priceType);
    if (!price) return null;

    return {
        visual: `Ord. pris ${Helpers.toEcommercePriceWithCurrency(price, 'hideRest')}/st`,
        a11y: `Ordinarie pris ${Helpers.toEcommercePriceWithCurrencyAriaLabel(price)} styck`,
    };
};

export const getProductRecycleFeeText = (
    product: {
        recycleFeeData: PriceData | undefined;
    },
    priceType: PriceType,
) => {
    if (!product.recycleFeeData) return null;

    const price = Helpers.getPrice(product.recycleFeeData, priceType);
    if (!price) return null;

    return getRecycleFeeText(price);
};

export const getRecycleFeeText = (price: number) => {
    return {
        visual: `+ ${Helpers.toEcommercePriceWithCurrency(price, 'hideRest')} pant`,
        a11y: `Plus ${Helpers.toEcommercePriceWithCurrencyAriaLabel(price)} pant`,
    };
};

export const getExcludingTaxText = (priceType: PriceType) => {
    if (priceType !== PriceType.ExcludeTax) return null;

    return {
        visual: 'Exkl. moms',
        a11y: 'Exklusive moms',
    };
};

export const getProductMaxPromoUsageText = (product: {
    promotion:
        | {
              maxUseText?: string;
          }
        | undefined;
}) => {
    const maxPromoUsage = product.promotion?.maxUseText;

    if (maxPromoUsage === undefined) return null;

    return getMaxPromoUsageText(maxPromoUsage);
};

export const getMaxPromoUsageText = (value: string) => {
    return {
        visual: `Erbj: Max ${value}/hushåll`,
        a11y: `Erbjudandet är begränsat till Max ${value} per hushåll`,
    };
};

export const getProductPackageSizeInfoText = (product: {
    packageSizeInformation: string | undefined;
    packageSize:
        | {
              unit: string;
              size?: number;
          }
        | undefined;
}) => {
    if (!product.packageSizeInformation) return null;

    return {
        visual: Helpers.capitalize(product.packageSizeInformation),
        a11y: Helpers.capitalize(product.packageSizeInformation),
        // TODO size and unit fields might contain diffrently formatted data, like "1000" and "MLT" where packageSizeInformation is "1 l".
        // Refactor to use separate fields when they are consistent with what's in packageSizeInformation
        // a11y: product.packageSize?.size
        //     ? Helpers.capitalize(`${product.packageSize.size} ${product.packageSize.unit}`)
        //     : Helpers.capitalize(product.packageSizeInformation),
    };
};

type ComparativePriceUnitParams = {
    unit: ProductComparativePriceUnit | null;
    postfix: string | null;
    fallbackText: string;
};

export type ProductComparativePriceTextParams = ComparativePriceUnitParams & {
    priceData: PriceData;
    priceType: PriceType;
    basedOn: 'regular' | 'promotion' | 'default';
};

export type ProductWeightPriceTextParams = ComparativePriceUnitParams & {
    priceData: PriceData | undefined;
    priceType: PriceType;
};

type FormatComparativePriceAndUnitParams = ComparativePriceUnitParams & {
    price: number;
};

type FormattedComparativePriceTextParams = FormatComparativePriceAndUnitParams & {
    basedOn: 'regular' | 'promotion' | 'default';
};

export const getProductComparativePriceText = ({
    priceData,
    unit,
    postfix,
    fallbackText,
    priceType,
    basedOn,
}: ProductComparativePriceTextParams) => {
    const resolvedComparativePrice = priceData && Helpers.getPrice(priceData, priceType);

    if (!resolvedComparativePrice) {
        return null;
    }
    const comparativeTexts = getFormattedComparativePriceText({
        price: resolvedComparativePrice,
        unit,
        postfix,
        fallbackText,
        basedOn,
    });

    return {
        visual: comparativeTexts.visual,
        a11y: comparativeTexts.a11y,
    };
};

export const getProductWeightPriceText = ({
    priceData,
    unit,
    postfix,
    fallbackText,
    priceType,
}: ProductWeightPriceTextParams) => {
    const resolvedComparativePrice = priceData && Helpers.getPrice(priceData, priceType);

    if (!resolvedComparativePrice) {
        return null;
    }

    const priceFormatted = Helpers.toEcommerceAriaPriceWithCurrency(resolvedComparativePrice);
    const formattedUnit = getFormattedPerComparativePriceUnit({ unit, postfix, fallbackText });

    const visualText = Helpers.toEcommercePrice(resolvedComparativePrice, 'hideRest');
    const a11yText = `${priceFormatted.a11y}${formattedUnit.a11y}`;

    return {
        visual: visualText,
        a11y: a11yText,
    };
};

export const getHistoricalPriceText = (
    product: { historicalPriceData: PriceData | undefined },
    priceType: PriceType,
) => {
    const resolvedHistoricalPrice =
        product.historicalPriceData && Helpers.getPrice(product.historicalPriceData, priceType);

    if (!resolvedHistoricalPrice) return null;

    return {
        visual: `Tidigare lägsta pris ${Helpers.toEcommercePriceWithCurrency(resolvedHistoricalPrice, 'hideRest')}/st`,
        a11y: `Tidigare lägsta pris ${Helpers.toEcommercePriceWithCurrencyAriaLabel(resolvedHistoricalPrice)} styck`,
    };
};

const formatComparativePriceAndUnit = ({
    price,
    unit,
    postfix,
    fallbackText,
}: FormatComparativePriceAndUnitParams) => {
    const priceFormatted = Helpers.toEcommerceAriaPriceWithCurrency(price);
    const formattedUnit = getFormattedPerComparativePriceUnit({ unit, postfix, fallbackText });

    return {
        visual: priceFormatted.visual + formattedUnit.visual,
        a11y: priceFormatted.a11y + formattedUnit.a11y,
    };
};

const getFormattedComparativePriceText = ({
    price,
    unit,
    postfix,
    fallbackText,
    basedOn,
}: FormattedComparativePriceTextParams) => {
    const { visual: visualText, a11y: a11yText } = formatComparativePriceAndUnit({
        price,
        unit,
        postfix,
        fallbackText,
    });

    switch (basedOn) {
        case 'regular':
            return {
                visual: `Ord. jfr pris ${visualText}`,
                a11y: `Ordinarie jämförpris ${a11yText}`,
            };
        case 'promotion':
            return {
                visual: `Erbj. jfr pris ${visualText}`,
                a11y: `Erbjudande jämförpris ${a11yText}`,
            };
        case 'default':
            return {
                visual: `Jfr pris ${visualText}`,
                a11y: `Jämförpris ${a11yText}`,
            };
        default: {
            return Helpers.exhaustiveCheck(basedOn);
        }
    }
};

const getFormattedPerComparativePriceUnit = ({
    unit,
    postfix,
    fallbackText,
}: ComparativePriceUnitParams) => {
    const formattedUnit = unit && getFormattedComparativePriceUnit(unit, postfix);
    if (!formattedUnit) {
        const fallbackWithoutCurrency = fallbackText.split('kr/')[1]; // "kronor" is already rendered so we need to remove it if its there

        return {
            visual: `/${fallbackWithoutCurrency || fallbackText}`,
            a11y: ` per ${fallbackWithoutCurrency || fallbackText}`,
        };
    }

    return {
        visual: `/${formattedUnit.visual}`,
        a11y: ` ${unit !== 'styck' ? 'per ' : ''}${formattedUnit.a11y}`, // per is not needed for 'styck'
    };
};

const getFormattedComparativePriceUnit = (
    unit: ProductComparativePriceUnit,
    postfix: string | null,
) => {
    const postfixFormatted = postfix ? ` ${postfix}` : '';

    let a11yText = '';
    let visualText = '';

    switch (unit) {
        case 'liter':
            visualText = 'lit';
            a11yText = 'liter';
            break;
        case 'kg':
            visualText = 'kg';
            a11yText = 'kilo';
            break;
        case 'styck':
            visualText = 'st';
            a11yText = 'styck';
            break;
        case 'meter':
            visualText = 'm';
            a11yText = 'meter';
            break;
        case 'disk':
        case 'tvätt':
        default:
            a11yText = unit;
            visualText = unit;
            break;
    }

    return {
        visual: visualText + postfixFormatted,
        a11y: a11yText + postfixFormatted,
    };
};

export const addSwedishLabel = (data: {
    fromSweden: boolean;
    attributeLabels: ProductAttributeLabel[];
}) => {
    const attributeLabels = [
        data.fromSweden &&
            ({
                identifier: 'sv',
                name: 'Producerad i Sverige',
                image: {
                    url: '/Assets/Icons/flag-sweden.svg',
                },
            } satisfies ProductAttributeLabel),
        ...data.attributeLabels,
    ].filter(Boolean);

    return attributeLabels;
};

export const getProductAttributesA11yText = (product: {
    attributeLabels: ProductAttributeLabel[];
}) => {
    return getAttributesA11yText(product.attributeLabels);
};

export const getAttributesA11yText = (attributeLabels: ProductAttributeLabel[]) => {
    if (!attributeLabels.length) return undefined;

    return `Märkt med ${attributeLabels
        .map((label) => label.name)
        .filter(Boolean)
        .join(' ')}`;
};

export const getProductSustainabilityA11yText = (product: {
    sustainabilityInfo?:
        | {
              score?: number | null;
              param: string;
              paramId: string;
          }[]
        | undefined;
}) => {
    if (!product.sustainabilityInfo) return 'Hållbarhetsdeklaration saknas';

    return `Hållbarhetsdeklaration ${getSustainabilityA11yLabelFromScores(product.sustainabilityInfo)}`;
};
